import { Phone, Plus, Trash2 } from 'lucide-react';
import { nanoid } from 'nanoid';
import type React from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ControllerRenderProps, FieldValues } from 'react-hook-form';
import { useIntl } from '@/core/i18n/use-intl';
import type { PhoneNumberEntry } from '@/shared/schema';
import { cn, normalizePhoneNumber } from '@/shared/utils';
import { Button } from '../../button';
import { Card, CardContent } from '../../card';
import { FormItem, FormMessage } from '../../form';
import { Input } from '../../input';
import { Label } from '../../label';
import AutoFormLabel from '../common/label';
import type { AutoFormInputComponentProps } from '../types';

type EditablePhoneNumber = {
  id: string;
  name: string;
  phone: string;
};

function ensurePhoneNumbersArray(value: unknown): EditablePhoneNumber[] {
  if (!value || !Array.isArray(value)) {
    return [];
  }

  return value
    .filter(
      (item): item is PhoneNumberEntry =>
        item && typeof item === 'object' && typeof (item as PhoneNumberEntry).id === 'string'
    )
    .map((entry) => ({
      id: entry.id,
      name: entry.name ?? '',
      phone: entry.phone ?? '',
    }));
}

function createEmptyPhoneNumber(): EditablePhoneNumber {
  return {
    id: nanoid(8),
    name: '',
    phone: '',
  };
}

const toStoredPhoneNumbers = (
  phoneNumbers: EditablePhoneNumber[]
): Array<Record<string, unknown>> | undefined => {
  const cleaned = phoneNumbers.map((entry) => {
    const phone = entry.phone.trim();
    return {
      id: entry.id,
      name: entry.name ?? '',
      ...(phone ? { phone } : {}),
    };
  });

  return cleaned.length > 0 ? cleaned : undefined;
};

interface PhoneNumberInputProps {
  id: string;
  testId: string;
  initialValue: string;
  onValueChange: (value: string) => void;
  transform?: (value: string) => string;
  placeholder: string;
  disabled?: boolean;
  label: string;
  labelClassName?: string;
  type?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
}

const PhoneNumberInput = memo(function PhoneNumberInput({
  id,
  testId,
  initialValue,
  onValueChange,
  transform,
  placeholder,
  disabled,
  label,
  labelClassName,
  type,
  inputMode,
}: PhoneNumberInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const lastReportedValue = useRef(initialValue);

  useEffect(() => {
    if (inputRef.current && document.activeElement !== inputRef.current) {
      if (inputRef.current.value !== initialValue) {
        inputRef.current.value = initialValue;
        lastReportedValue.current = initialValue;
      }
    }
  }, [initialValue]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = transform ? transform(e.target.value) : e.target.value;
      if (transform && nextValue !== e.target.value) {
        e.target.value = nextValue;
      }
      if (nextValue !== lastReportedValue.current) {
        lastReportedValue.current = nextValue;
        onValueChange(nextValue);
      }
    },
    [onValueChange, transform]
  );

  return (
    <div className="space-y-1">
      <Label htmlFor={id} className={cn('text-xs', labelClassName)}>
        {label}
      </Label>
      <Input
        ref={inputRef}
        id={id}
        data-testid={testId}
        defaultValue={initialValue}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        type={type}
        inputMode={inputMode}
        className="h-9 md:h-8"
      />
    </div>
  );
});

const PhoneNumberRow = memo(function PhoneNumberRow({
  entry,
  index,
  onUpdate,
  onRemove,
  disabled,
}: {
  entry: EditablePhoneNumber;
  index: number;
  onUpdate: (id: string, updates: Partial<EditablePhoneNumber>) => void;
  onRemove: (id: string) => void;
  disabled?: boolean;
}) {
  const { t } = useIntl();
  const entryId = entry.id;

  return (
    <div
      className="grid gap-2 p-2 rounded-lg border bg-muted/20"
      data-testid={`phone-number-row-${index}`}
    >
      <div className="grid gap-2 md:items-center md:grid-cols-[1fr_1fr_auto]">
        <PhoneNumberInput
          id={`phone-${entryId}-name`}
          testId={`phone-number-${index}-name`}
          initialValue={entry.name ?? ''}
          onValueChange={(value) => onUpdate(entryId, { name: value })}
          placeholder={t('metadata.phoneNumbers.namePlaceholder')}
          disabled={disabled}
          label={t('metadata.phoneNumbers.name')}
          labelClassName="md:hidden"
        />

        <PhoneNumberInput
          id={`phone-${entryId}-phone`}
          testId={`phone-number-${index}-phone`}
          initialValue={entry.phone ?? ''}
          onValueChange={(value) => onUpdate(entryId, { phone: value })}
          transform={normalizePhoneNumber}
          placeholder={t('metadata.phoneNumbers.phonePlaceholder')}
          disabled={disabled}
          label={t('metadata.phoneNumbers.phone')}
          labelClassName="md:hidden"
          type="tel"
          inputMode="numeric"
        />

        <div className="flex items-end gap-1 md:items-center justify-end pt-2 md:pt-0">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onRemove(entryId)}
            disabled={disabled}
            data-testid={`phone-number-${index}-remove`}
            className="h-9 w-9 md:h-8 md:w-8 p-0 text-muted-foreground hover:text-destructive"
            title={t('common.delete')}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
});

export default function AutoFormPhoneNumbers({
  label,
  isRequired,
  fieldConfigItem,
  field,
  fieldProps,
}: AutoFormInputComponentProps & {
  field: ControllerRenderProps<FieldValues, string>;
}) {
  const { t } = useIntl();
  // Strip the react-hook-form field handlers before spreading onto the wrapper
  // element: `fieldProps` includes `...field`, and letting `onChange` land on the
  // <div> makes bubbling input events call `field.onChange(event)`, which clobbers
  // the phone-number array with a raw input string.
  const {
    showLabel: _showLabel,
    disabled,
    onChange: _onChange,
    onBlur: _onBlur,
    value: _value,
    name: _name,
    ref: _ref,
    ...restFieldProps
  } = fieldProps;
  const showLabel = _showLabel === undefined ? true : _showLabel;

  const [phoneNumbers, setPhoneNumbers] = useState<EditablePhoneNumber[]>(() =>
    ensurePhoneNumbersArray(field.value)
  );
  const isOwnUpdateRef = useRef(false);

  const fieldValueIds = useMemo(() => {
    return ensurePhoneNumbersArray(field.value)
      .map((entry) => entry.id)
      .join(',');
  }, [field.value]);

  const localIds = useMemo(() => phoneNumbers.map((entry) => entry.id).join(','), [phoneNumbers]);

  useEffect(() => {
    if (isOwnUpdateRef.current) {
      isOwnUpdateRef.current = false;
      return;
    }

    const nextPhoneNumbers = ensurePhoneNumbersArray(field.value);
    if (fieldValueIds !== localIds || phoneNumbers.length !== nextPhoneNumbers.length) {
      setPhoneNumbers(nextPhoneNumbers);
    }
  }, [field.value, fieldValueIds, localIds, phoneNumbers.length]);

  const syncToForm = useCallback(
    (nextPhoneNumbers: EditablePhoneNumber[]) => {
      isOwnUpdateRef.current = true;
      field.onChange(toStoredPhoneNumbers(nextPhoneNumbers));
    },
    [field]
  );

  const updatePhoneNumbers = useCallback(
    (updater: (previous: EditablePhoneNumber[]) => EditablePhoneNumber[]) => {
      setPhoneNumbers((previous) => {
        const next = updater(previous);
        syncToForm(next);
        return next;
      });
    },
    [syncToForm]
  );

  return (
    <FormItem className="col-span-6" {...restFieldProps}>
      <Card className="border-border/60" data-testid="phone-numbers-field">
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-3 mb-2">
            {showLabel && (
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
                  <Phone className="size-4 text-primary" />
                </div>
                <AutoFormLabel
                  label={fieldConfigItem?.label || label}
                  isRequired={isRequired}
                  className="text-sm font-semibold"
                />
              </div>
            )}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => updatePhoneNumbers((prev) => [...prev, createEmptyPhoneNumber()])}
              disabled={disabled}
              data-testid="add-phone-number-button"
              className="h-8"
            >
              <Plus className="size-3.5 mr-1.5" />
              {t('metadata.phoneNumbers.addPhoneNumber')}
            </Button>
          </div>
          {phoneNumbers.length === 0 ? (
            <p
              className="text-sm text-muted-foreground py-4 text-center"
              data-testid="no-phone-numbers-message"
            >
              {t('metadata.phoneNumbers.noPhoneNumbers')}
            </p>
          ) : (
            <div className="space-y-3">
              <div className="hidden md:grid gap-2 px-2 md:grid-cols-[1fr_1fr_auto]">
                <span className="text-xs font-medium text-muted-foreground">
                  {t('metadata.phoneNumbers.name')}
                </span>
                <span className="text-xs font-medium text-muted-foreground pl-1">
                  {t('metadata.phoneNumbers.phone')}
                </span>
                <div className="w-[36px]" />
              </div>
              {phoneNumbers.map((entry, index) => (
                <PhoneNumberRow
                  key={entry.id}
                  entry={entry}
                  index={index}
                  onUpdate={(id, updates) =>
                    updatePhoneNumbers((prev) =>
                      prev.map((item) => (item.id === id ? { ...item, ...updates } : item))
                    )
                  }
                  onRemove={(id) =>
                    updatePhoneNumbers((prev) => prev.filter((item) => item.id !== id))
                  }
                  disabled={disabled}
                />
              ))}
            </div>
          )}

          <FormMessage />
        </CardContent>
      </Card>
    </FormItem>
  );
}
